<template>
  <div class="component-name">
    <div v-if="!has_basis || !has_purchase_date" class="notification is-warning">
      <p v-if="!has_basis && !has_purchase_date">
        <strong>Warning:</strong> The initial portfolio lacks both cost basis and purchase date information. This may affect the accuracy of tax calculations.
      </p>
      <p v-else-if="!has_basis">
        <strong>Warning:</strong> The initial portfolio lacks cost basis information. This may affect the accuracy of tax calculations.
      </p>
      <p v-else>
        <strong>Warning:</strong> The initial portfolio lacks purchase date information. By default, we assume all gains and losses are long-term gains and losses. This may affect the accuracy of tax calculations.
      </p>
    </div>
    <div v-if="notificationMessage" class="notification is-warning">
      <p>{{ notificationMessage }}</p>
    </div>

    <div class="tw-flex tw-justify-center tw-gap-10 tw-py-10">
      <div class="tw-flex tw-flex-col tw-flex-1 tw-p-3 tw-max-w-md tw-bg-gray-200">
        <span class="tw-font-bold">Starting Portfolio:</span>
        <span class="tw-text-xl">{{ checkupStore.portfolio?.name }}</span>
      </div>
      <div class="tw-flex tw-flex-col tw-flex-1 tw-p-3 tw-max-w-md tw-bg-gray-200">
        <span class="tw-font-bold">Destination Portfolio:</span>
        <span class="tw-text-xl">{{ checkupStore.optimizedPortfolio?.name }}</span>
      </div>
    </div>

    <div class="grid-container">
      <!-- row 1-->
      <div>
        <p class="heading">
          Portfolio Size
        </p>
        <p
          v-if="portfolio_size"
          class="is-size-5 has-text-weight-semibold"
        >
          {{ portfolio_size?.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }) }}
        </p>
      </div>
      <p
        v-for="port in transitions"
        :key="port.label + ' 1'"
        class="title dm-serif-display"
      >
        {{ port.label }}
      </p>

      <!-- row 2-->
      <p class="dm-serif-display is-size-4">
        Tax Cost Today
      </p>

      <div
        v-for="port in transitions"
        :key="port.label + ' 2'"
      >
        <p class="title is-5" v-if="port.label != 'Gradual Budgeted'">
          {{ port.tax_budget?.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }) }}
        </p>
        <div
          v-if="port.label == 'Gradual Budgeted'"
        >
          <label class="label tw-flex tw-justify-between tw-items-center">
            <div>
              <span>Budget</span>
              <i
                class="fa fa-edit tw-text-[#F1583D] tw-pl-1"
                title="Edit the budget below"
              />
            </div>
            <i
              v-if="updating"
              class="fa fa-spinner fa-spin tw-text-gray-500"
            />
          </label>
          <div class="tw-relative">
            <span
              class="tw-absolute tw-h-full tw-flex tw-items-center tw-px-1"
            >$</span>
            <input
              v-model="tax_budget"
              type="number"
              min="0"
              step="100"
              :class="[
                'title is-5',
                'tw-pl-5 tw-py-1',
                'tw-w-full tw-bg-white tw-bg-opacity-50',
                'tw-border tw-border-solid tw-border-gray-300 tw-rounded'
              ]"
              @input="handleBudgetChange"
            >
          </div>
        </div>
      </div>

      <div>
        <p class="dm-serif-display is-size-4">
          Portion of Portfolio Transitioned
        </p>
        <p class="subtitle is-6 is-italic">
          Approximate years to full transition
        </p>
      </div>

      <div
        v-for="port in transitions"
        :key="port.label + ' 3'"
      >
        <p
          v-if="port.transition_pct"
          class="title is-5"
        >
          {{ (100*port.transition_pct).toFixed(2) }} %
        </p>

        <p
          v-if="port.transition_pct == 0"
          class="subtitle is-6 is-italic"
        />
        <p
          v-else-if="typeof(port?.years) == 'number' && !isNaN(port.years)"
          class="subtitle is-6 is-italic"
        >
          {{ port.years.toFixed(1) }} years
        </p>
      </div>


      <div>
        <p class="dm-serif-display is-size-4">
          Tracking Error Today
        </p>
        <p class="is-6 is-italic">
          Today's Portfolio vs. Destination Portfolio
        </p>
        <p class="tw-text-gray-500">
          <span>Current is </span>
          <span>{{ (initialTrackingError * 100).toFixed(2) }}%</span>
        </p>
      </div>


      <div
        v-for="port in transitions"
        :key="port.label + ' 4'"
      >
        <p
          v-if="typeof(port?.te) == 'number'"
          class="title is-5"
        >
          {{ (port.te *100).toFixed(2) }} %
        </p>
      </div>
      <div class="dm-serif-display is-size-4" />
    </div>
    <div class="buttons is-right">
      <button
        class="button is-dark is-outlined is-small mt-0 pt-0"
        @click="exportPortfolioToCSV"
        :disabled="!spreadsheet_task || taskStatus !== 'completed'"
      >
        <span v-if="taskStatus === 'pending'">Preparing Download...</span>
        <span v-else-if="taskStatus === 'completed'">Download Trades</span>
        <span v-else-if="taskStatus === 'failed'">Download Failed</span>
        <span v-else>Calculate First</span>
      </button>
    </div>


    <div class="activeBorder" />
  </div>
</template>

<script>
import { mapStores } from 'pinia';
import { useCheckupStore } from '@frontend/ui/stores/checkupStore';
import debounce from 'lodash/debounce';

export default {
  name: 'ComponentName',
  props: {
    longTermRate: {
      type: [Number, String],
      default: 0,
    },
    shortTermRate: {
      type: [Number, String],
      default: 0,
    },
  },
  data() {
    return {
      initial_portfolio: false,
      initialTrackingError: null,
      optimizedPortfolio: false,
      tax_budget: 10000,
      portfolio_size: '',
      starting_portfolio: {},
      notificationMessage: '',
      transitions: [
        { 'label': 'Minimal Taxes' },
        { 'label': 'Gradual Budgeted' },
        { 'label': 'Full Transition Today' },
      ],
      spreadsheet_task: false,
      taskStatus: 'pending',
      updating: false,
      has_basis: true,
      has_purchase_date: true,
    };
  },
  computed: {
    ...mapStores(useCheckupStore),
  },
  watch: {
    'checkupStore.portfolio': {
      handler: function(portfolio) {
        console.log('Merged portfolio created:', portfolio);
        if (portfolio) this.selectInitialPortfolio(portfolio);
      },
      immediate: true,
    },
    'checkupStore.optimizedPortfolio': {
      handler: function(optimizedPortfolio) {
        console.log('optimizedPortfolio selected:', optimizedPortfolio);
        if (optimizedPortfolio) this.selectOptimizedPortfolio(optimizedPortfolio);
      },
      immediate: true,
    },
    longTermRate() {
      this.debouncedUpdate();
    },
    shortTermRate() {
      this.debouncedUpdate();
    },
  },
  created() {
    this.debouncedUpdate = debounce(this.update, 500);
  },
  methods: {
    validateInputs() {
      // Clear previous errors
      this.notificationMessage = '';

      // Check portfolios exist
      if (!this.initial_portfolio?.external_id) {
        this.notificationMessage = 'Please select a starting portfolio.';
        return false;
      }
      if (!this.optimizedPortfolio?.external_id) {
        this.notificationMessage = 'Please select a destination portfolio.';
        return false;
      }

      // Validate tax budget
      const budget = parseFloat(this.tax_budget);
      if (isNaN(budget)) {
        this.notificationMessage = 'Please enter a valid tax budget amount.';
        return false;
      }
      if (budget < 0) {
        this.notificationMessage = 'Tax budget cannot be negative.';
        return false;
      }

      // Validate tax rates (they come as percentages, so 0-100 range)
      const ltRate = parseFloat(this.longTermRate);
      const stRate = parseFloat(this.shortTermRate);

      if (isNaN(ltRate) || ltRate < 0 || ltRate > 100) {
        this.notificationMessage = 'Long-term tax rate must be between 0% and 100%.';
        return false;
      }
      if (isNaN(stRate) || stRate < 0 || stRate > 100) {
        this.notificationMessage = 'Short-term tax rate must be between 0% and 100%.';
        return false;
      }

      return true;
    },
    selectInitialPortfolio(portfolio) {
      this.initial_portfolio = portfolio;
      this.update();
    },
    selectOptimizedPortfolio(optimizedPortfolio) {
      this.optimizedPortfolio = optimizedPortfolio;
      this.update();
    },
    handleBudgetChange() {
      console.log('Budget changed:', this.tax_budget);
      this.debouncedUpdate();
    },
    clearCalculatedData() {
      // Clear all calculated fields to prevent showing stale data
      this.transitions = [
        { 'label': 'Minimal Taxes' },
        { 'label': 'Gradual Budgeted' },
        { 'label': 'Full Transition Today' },
      ];
      this.portfolio_size = '';
      this.starting_portfolio = {};
      this.initialTrackingError = null;
      this.spreadsheet_task = false;
      this.taskStatus = 'pending';
      this.checkupStore.taxTransitionPlan = {};
    },
    async update() {
      if (this.initial_portfolio && this.optimizedPortfolio && this.optimizedPortfolio?.external_id.startsWith('entity_')) {
        // Validate inputs before making API call
        if (!this.validateInputs()) {
          this.updating = false;
          this.clearCalculatedData();
          return;
        }

        // Clear any previous error messages when starting a new calculation
        this.notificationMessage = '';
        this.updating = true;
        const transitions = [];
        let url = '/api/v1/tax_transition/?';
        const params = new URLSearchParams({
          'initial_portfolio': this.initial_portfolio.external_id,
          'destination': this.optimizedPortfolio.external_id,
          'tax_budget': this.tax_budget || 0,
          'long_term_rate': this.longTermRate / 100,
          'short_term_rate': this.shortTermRate / 100,
        });
        url = url + params.toString();
        const res = await fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': this.$root.$data.csrfToken,
          },
        });
        const j = await res.json();
        if ('error' in j) {
          this.updating = false;
          // Use the error message from backend if available, otherwise use default
          this.notificationMessage = j.error || "An error occurred calculating the transition plan. Please try again.";
          this.clearCalculatedData();
          return;
        }
        const t = j['transitions'];
        transitions.push({ 'label': this.transitions[0].label, 'transition_pct': t[0]['pct'], 'tax_budget': 0, 'te': t[0]['te'], 'years': '', 'transition': t[0]['transition'] });
        let years = (t[2]['taxes']/t[1]['taxes']);
        if (years < 0) {
          years = 0;
        }
        console.log('After years');
        console.log('YEARSSSS', years);
        transitions.push({ 'label': this.transitions[1].label, 'transition_pct': t[1]['pct'], 'tax_budget': t[1]['taxes'], 'te': t[1]['te'], 'years': years, 'transition': t[1]['transition'] });
        transitions.push({ 'label': this.transitions[2].label, 'transition_pct': 1, 'tax_budget': t[2]['taxes'], 'te': 0, 'years': '0', 'transition': t[2]['transition'] });

        this.portfolio_size = j['portfolio_size'];
        this.transitions = [...transitions];
        this.checkupStore.taxTransitionPlan = this.formatTransitionPlan();
        this.starting_portfolio = j['initial_portfolio'];
        this.spreadsheet_task = j['spreadsheet_task'];
        this.taskStatus = 'pending';
        this.checkTaskStatus();
        this.initialTrackingError = j['initial_tracking_error'];
        this.has_basis = j['has_basis'];
        this.has_purchase_date = j['has_purchase_date'];
        this.updating = false;
      }
    },
    formatTransitionPlan() {
      const columns = ['Minimal Taxes', 'Gradual Budgeted', 'Full Transition Today'];

      const rowsByName = {
        cost: ['Tax Cost Today'],
        transitioned: ['Percent Transitioned'],
        years: ['Approximate Years'],
        trackingError: ['Tracking Error Today'],
      };

      for (const column of columns) {
        const transition = this.transitions.find(t => t.label === column);
        if (!transition || typeof transition.tax_budget !== 'number') {
            continue;
        }
        const budget = transition.tax_budget.toLocaleString('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        });

        let years = transition?.years;
        years = years ? parseInt(years).toFixed(1) : '0';

        const transitioned = transition.transition_pct.toLocaleString('en-US', {
          style: 'percent',
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        });

        const trackingError = transition.te.toLocaleString('en-US', {
          style: 'percent',
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        });

        rowsByName.cost.push(budget);
        rowsByName.transitioned.push(transitioned);
        rowsByName.years.push(years);
        rowsByName.trackingError.push(trackingError);
      }

      return [
        ['', ...columns],
        rowsByName.cost,
        rowsByName.transitioned,
        rowsByName.years,
        rowsByName.trackingError,
      ];
    },
    exportPortfolioToCSV() {
      if (this.spreadsheet_task && this.taskStatus === 'completed') {
        this.downloadCSV();
      } else {
        alert('Please wait for the calculation to complete.');
      }
    },
    downloadCSV() {
      const link = document.createElement('a');
      const filename = 'transition.csv';
      link.setAttribute('href', '/api/v1/fetch_tax_transition_sheet?task_id=' + this.spreadsheet_task);
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    async checkTaskStatus() {
      if (!this.spreadsheet_task) return;

      const url = `/api/v1/tasks/${this.spreadsheet_task}/`;
      const res = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': this.$root.$data.csrfToken,
        },
      });
      const data = await res.json();

      if (data.task.status === 'complete') {
        this.taskStatus = 'completed';
      } else if (data.task.status === 'failed') {
        this.taskStatus = 'failed';
      } else {
        // If the task is still pending, check again after a delay
        setTimeout(() => this.checkTaskStatus(), 5000); // Check every 5 seconds
      }
    },
  },
};
</script>

<style scoped>
.grid-container {
  display: grid;
  grid-template-columns: auto repeat(3, 1fr);
  gap: 2em;
  padding: 2em;
}

/* Style for the first column (labels) */
.grid-container > :nth-child(4n + 1) {
  grid-column: 1;
}

/* No Net Taxes column */
.grid-container > :nth-child(4n + 2) {
  grid-column: 2;
  background-color: #3273dc;
  color: white !important;
  min-width: 8em;
}
.grid-container > :nth-child(4n + 2) >  p.title {
  color: white !important;
}

/* Gradual Budgeted column */
.grid-container > :nth-child(4n + 3) {
  grid-column: 3;
  background-color: #eef3fc;
  min-width: 8em;
}

/* Instant Budgetless column */
.grid-container > :nth-child(4n + 4) {
  grid-column: 4;
  background-color: white;
  border: 2px solid #3273dc;
  min-width: 8em;
}

/* Remove gaps within columns */
.grid-container > * {
  margin: 0;
  padding: 1em;
}

/* Title row styling */
.grid-container > :nth-child(-n+4) {
  font-weight: bold;
  font-size: 1.2em;
  align-self: start;
}

/* Align content vertically */
.grid-container > * {
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.notification {
  margin-bottom: 1rem;
}

.button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>